"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState, useEffect } from "react";
import { Calendar, MapPin, Ticket, Clock } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function CountdownConsumer() {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  const [timeLeft, setTimeLeft] = useState({
    d: 0,
    h: 0,
    m: 0,
    s: 0
  });
  const [progress, setProgress] = useState(100);
  useEffect(() => {
    const targetDate = new Date().getTime() + 3 * 24 * 60 * 60 * 1000 + 14 * 60 * 60 * 1000;
    const totalDuration = 7 * 24 * 60 * 60 * 1000;
    const timer = setInterval(() => {
      const now = new Date().getTime();
      const distance = targetDate - now;
      if (distance < 0) {
        clearInterval(timer);
        setTimeLeft({
          d: 0,
          h: 0,
          m: 0,
          s: 0
        });
        setProgress(0);
        return;
      }
      setTimeLeft({
        d: Math.floor(distance / (1000 * 60 * 60 * 24)),
        h: Math.floor(distance % (1000 * 60 * 60 * 24) / (1000 * 60 * 60)),
        m: Math.floor(distance % (1000 * 60 * 60) / (1000 * 60)),
        s: Math.floor(distance % (1000 * 60) / 1000)
      });
      setProgress(Math.max(0, distance / totalDuration * 100));
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  if (loading) {
    return <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center min-h-[60vh]">
          <div className="aspect-video lg:aspect-square rounded-md bg-muted/10 animate-pulse" />
          <div className="space-y-8">
            <div className="h-12 bg-muted/10 rounded-md w-3/4 animate-pulse" />
            <div className="h-32 bg-muted/10 rounded-md w-full animate-pulse" />
            <div className="h-12 bg-muted/10 rounded-md w-1/2 animate-pulse" />
          </div>
        </div>
      </div>;
  }
  if (error || !products || products.length === 0) {
    return null;
  }
  const event = products[0];
  return <div className="container mx-auto px-4 relative z-10">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        {}
        <div className="relative group perspective-1000">
          <div className="absolute -inset-1 bg-gradient-to-r from-primary/20 to-accent/20 rounded-md blur-xl opacity-30 group-hover:opacity-50 transition duration-1000"></div>
          <div className="relative aspect-video lg:aspect-[4/5] overflow-hidden rounded-md border border-border/20 bg-card shadow-xl shadow-black/20">
            <img src={event.featured_image_url || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680521/img/wedding_reception_venue.png"} alt={event.title} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
            
            <div className="absolute bottom-0 left-0 w-full p-6 lg:p-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-primary/10 border border-primary/20 text-primary text-xs font-default font-medium uppercase tracking-widest mb-4 backdrop-blur-sm">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-lg bg-primary opacity-75"></span>
                  <span className="relative inline-flex rounded-lg h-2 w-2 bg-primary"></span>
                </span>
                The Main Event
              </div>
              <h2 className="text-3xl md:text-5xl font-serif font-bold text-foreground mb-2 leading-tight">
                {event.title}
              </h2>
            </div>
          </div>
        </div>

        {}
        <div className="flex flex-col justify-center space-y-10">
          
          {}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="flex flex-col p-4 rounded-md bg-card/30 border border-border/10 backdrop-blur-sm hover:bg-card/50 transition-colors">
              <SafeHydrate><Calendar className="w-5 h-5 text-primary mb-2" /></SafeHydrate>
              <span className="text-xs text-muted-foreground font-default uppercase tracking-wider">Date</span>
              <span className="text-base font-serif font-semibold text-foreground mt-1">
                {event.customFields?.date || "June 14, 2025"}
              </span>
            </div>
            <div className="flex flex-col p-4 rounded-md bg-card/30 border border-border/10 backdrop-blur-sm hover:bg-card/50 transition-colors">
              <MapPin className="w-5 h-5 text-primary mb-2" />
              <span className="text-xs text-muted-foreground font-default uppercase tracking-wider">Location</span>
              <span className="text-base font-serif font-semibold text-foreground mt-1 line-clamp-1">
                {event.customFields?.location || "The Grand Ballroom"}
              </span>
            </div>
            <div className="flex flex-col p-4 rounded-md bg-card/30 border border-border/10 backdrop-blur-sm hover:bg-card/50 transition-colors">
              <Ticket className="w-5 h-5 text-primary mb-2" />
              <span className="text-xs text-muted-foreground font-default uppercase tracking-wider">RSVP Status</span>
              <span className="text-base font-serif font-semibold text-foreground mt-1">
                Open Now
              </span>
            </div>
          </div>

          {}
          <div className="space-y-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xl font-serif font-semibold text-foreground flex items-center gap-2">
                <Clock className="w-5 h-5 text-primary" />
                Countdown to Celebration
              </h3>
            </div>
            
            <div className="grid grid-cols-4 gap-2 sm:gap-4 text-center">
              {[{
              label: "Days",
              value: timeLeft.d
            }, {
              label: "Hours",
              value: timeLeft.h
            }, {
              label: "Minutes",
              value: timeLeft.m
            }, {
              label: "Seconds",
              value: timeLeft.s
            }].map((unit, i) => <div key={unit.label} data-index={i} className="flex flex-col">
                  <div className="bg-secondary/40 border border-border/10 backdrop-blur-md rounded-md py-4 sm:py-6 flex items-center justify-center shadow-sm">
                    <span className="text-4xl sm:text-6xl font-serif font-light text-foreground tabular-nums tracking-tighter">
                      {unit.value.toString().padStart(2, "0")}
                    </span>
                  </div>
                  <span className="text-xs sm:text-sm text-muted-foreground font-default font-medium uppercase tracking-widest mt-3">
                    {unit.label}
                  </span>
                </div>)}
            </div>

            {}
            <div className="space-y-2 pt-4">
              <div className="flex justify-between text-xs font-default uppercase tracking-widest">
                <span className="text-primary">Guest List Filling</span>
                <span className="text-muted-foreground">Limited Seating</span>
              </div>
              <Progress value={progress} className="h-1.5 bg-secondary/50" />
            </div>
          </div>

          {}
          <div className="pt-6">
            <AddToCartButton productId={event.id} className="w-full" buttonClassName="w-full h-14 text-lg font-default font-bold uppercase tracking-widest bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg transition-all duration-500 rounded-md" text="Confirm Your Attendance" showQuantity={false} />
            <p className="text-center text-xs text-muted-foreground mt-4 font-default italic">
              Please RSVP by the end of the month to ensure your place.
            </p>
          </div>

        </div>
      </div>
    </div>;
}
export default function GlobalPremiereCountdown() {
  const wp_query_params = {
    per_page: 1,
    post_type: "product",
    orderby: "date",
    order: "desc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return;
}